import { useOutreachGmailStatus } from "@inrange/building-manager-api-client";

// Promoting is the start of a chain that ends in drafted emails, so it is blocked
// until there is a mailbox for those drafts to land in. Without this the operator
// finds out only after a batch has been written - which is the expensive moment to
// discover it, because the emails exist and are nowhere they can be sent from.
//
// A hook rather than a boolean computed per page: the rule is rendered on two
// surfaces that must agree - the bulk promote modal on /leads/search and the
// per-row Promote pair inside LeadEditPanel, which is mounted from BOTH
// /leads and /leads/search. Those were the copies that had diverged: the bulk
// button was gated and the per-row pair was not, so the page said "connect first"
// while its own primary button promoted anyway.
export const useOutreachPromoteGate = (): {
  blocked: boolean;
  reason: string;
} => {
  const status = useOutreachGmailStatus();
  // Fails closed: until the status has been read, "not connected" cannot be ruled
  // out, and a promote that slips through is exactly the batch of stranded drafts
  // this gate exists to prevent. Data first, so a failed background refetch does
  // not block over a status we already hold.
  if (!status.data) {
    return status.isError
      ? {
          blocked: true,
          reason:
            "Couldn't check the Gmail connection - retrying automatically. Promoting starts a run that ends in drafted emails, so it waits until we know they have somewhere to go.",
        }
      : { blocked: true, reason: "Checking the Gmail connection..." };
  }
  // An environment with no OAuth client at all has nothing an operator could
  // connect, so blocking there would take the whole lead pipeline down over a
  // feature that is not wired up yet. Only a CONFIGURED-but-unconnected env blocks.
  if (status.data.configured && !status.data.connected) {
    return {
      blocked: true,
      reason:
        "Connect the sending account first - it is at the top of the page. Promoting starts a run that ends in drafted emails, and with no mailbox connected those drafts have nowhere to go.",
    };
  }
  return { blocked: false, reason: "" };
};
